import {
  Dog, Cat, Bird, Fish, Rabbit, PawPrint, Sparkles, Heart, HelpCircle,
  Calendar, ShoppingBag, Camera, Users, Trophy, BookOpen
} from 'lucide-react';

export const PET_TYPES = [
  { id: 'dog', label: 'Dogs', icon: Dog, description: 'Canine companions' },
  { id: 'cat', label: 'Cats', icon: Cat, description: 'Feline friends' },
  { id: 'bird', label: 'Birds', icon: Bird, description: 'Feathered pets' },
  { id: 'fish', label: 'Fish', icon: Fish, description: 'Aquatic pets' },
  { id: 'rabbit', label: 'Rabbits', icon: Rabbit, description: 'Small mammals' },
  { id: 'reptile', label: 'Reptiles', icon: PawPrint, description: 'Scaled companions' },
  { id: 'rodent', label: 'Rodents', icon: PawPrint, description: 'Hamsters, gerbils, guinea pigs' },
  { id: 'horse', label: 'Horses', icon: PawPrint, description: 'Equine animals' },
  { id: 'exotic', label: 'Exotic Pets', icon: Sparkles, description: 'Unusual companions' },
  { id: 'multi', label: 'Multiple Types', icon: Heart, description: 'All pets welcome' },
  { id: 'other', label: 'Other', icon: HelpCircle, description: 'Specify your pet type' },
];

// Countries list (common ones)
export const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'JP', name: 'Japan' },
  { code: 'IN', name: 'India' },
  { code: 'OTHER', name: 'Other' },
];

// Languages list (ISO 639-1 codes)
export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'nl', name: 'Dutch' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ru', name: 'Russian' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hi', name: 'Hindi' },
  { code: 'other', name: 'Other' },
];

// US States
export const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// Club type options
export const CLUB_TYPES = [
  { id: 'standard', label: 'Standard Club', description: 'Community-based pet club', fee: '$99 setup' },
  { id: 'ngo', label: 'Non-Profit / Rescue', description: 'Animal welfare organization', fee: 'Free' },
  { id: 'breeder', label: 'Breeder Network', description: 'Professional breeding community', fee: '$199 setup' },
];

// Color presets
export const COLOR_PRESETS = [
  { name: 'Classic Brown', primary: '#c9762b', secondary: '#a65d1f', accent: '#e3c992', description: 'Warm & traditional' },
  { name: 'Forest Green', primary: '#166534', secondary: '#14532d', accent: '#86efac', description: 'Natural & fresh' },
  { name: 'Ocean Blue', primary: '#1e40af', secondary: '#1e3a8a', accent: '#93c5fd', description: 'Cool & professional' },
  { name: 'Royal Purple', primary: '#7c3aed', secondary: '#6d28d9', accent: '#c4b5fd', description: 'Bold & modern' },
  { name: 'Sunset Orange', primary: '#ea580c', secondary: '#c2410c', accent: '#fed7aa', description: 'Energetic & warm' },
  { name: 'Rose Pink', primary: '#db2777', secondary: '#be185d', accent: '#fbcfe8', description: 'Friendly & fun' },
  { name: 'Slate Gray', primary: '#475569', secondary: '#334155', accent: '#cbd5e1', description: 'Professional & neutral' },
  { name: 'Teal', primary: '#0d9488', secondary: '#0f766e', accent: '#99f6e4', description: 'Fresh & calming' },
];

// Feature options for initial setup
export const INITIAL_FEATURES = [
  { id: 'events', label: 'Events & Calendar', icon: Calendar, default: true },
  { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag, default: true },
  { id: 'photo_albums', label: 'Photo Albums', icon: Camera, default: true },
  { id: 'community_forums', label: 'Community Forums', icon: Users, default: true },
  { id: 'health_records', label: 'Health Records', icon: Heart, default: false },
  { id: 'breeding_tools', label: 'Breeding Tools', icon: PawPrint, default: false },
  { id: 'competitions', label: 'Competitions & Shows', icon: Trophy, default: false },
  { id: 'resources_library', label: 'Resources Library', icon: BookOpen, default: true },
];

// Why create club options
export const WHY_CREATE_OPTIONS = [
  { id: 'connect_owners', label: 'Connect pet owners in my area', description: 'Build a local community' },
  { id: 'organize_events', label: 'Organize events & meetups', description: 'Dog shows, playdates, training sessions' },
  { id: 'breed_preservation', label: 'Preserve & promote a specific breed', description: 'Breed education & standards' },
  { id: 'rescue_support', label: 'Support rescue & adoption efforts', description: 'Help animals find homes' },
  { id: 'professional_network', label: 'Network with breeders & professionals', description: 'Business & breeding connections' },
  { id: 'education', label: 'Share educational resources', description: 'Training, health, care information' },
  { id: 'competitions', label: 'Organize competitions & shows', description: 'Conformation, agility, obedience' },
  { id: 'hobby', label: 'Share a hobby or special interest', description: 'Hiking with dogs, pet photography, etc.' },
  { id: 'other', label: 'Other reason', description: 'Tell us more below' },
];

